import { useMemo } from 'react';

/** Fuzzy match: substring first (fast path), then character subsequence. */
function fuzzyMatch(text: string, query: string): boolean {
  if (text.includes(query)) return true;
  let qi = 0;
  for (let i = 0; i < text.length && qi < query.length; i++) {
    if (text[i] === query[qi]) qi++;
  }
  return qi === query.length;
}

/**
 * Caller-supplied search config. `getText` returns the searchable text for a
 * row (e.g. the campaign name); matching is case-insensitive fuzzy. Provide
 * a STABLE `getText` (module scope or useCallback) so the memo doesn't recompute
 * every render.
 */
export interface ClientTableSearch<T> {
  query: string;
  getText: (row: T) => string;
}

export interface UseClientTableOptions<T> {
  page: number;
  pageSize: number;
  search?: ClientTableSearch<T>;
}

export interface UseClientTableResult<T> {
  /** Rows for the current (clamped) page, after search. */
  pageRows: T[];
  /** Count of rows after search — drives the pagination control's page count. */
  total: number;
  /** Number of pages for `total` at `pageSize` (always ≥ 1). */
  pageCount: number;
}

/**
 * Pure, client-side table pipeline: search → paginate. Holds no state; runs
 * entirely in `useMemo` (derived values, no effects). Page count and total
 * derive from the POST-search set, so narrowing the search re-paginates
 * automatically. The page is clamped into [1, pageCount] before slicing, so a
 * stale page index can never render an empty page.
 *
 * Sort and column filters are intentionally NOT handled here yet (deferred);
 * add them as new pipeline stages before the paginate step when those land.
 */
export function useClientTable<T>(
  data: T[],
  { page, pageSize, search }: UseClientTableOptions<T>,
): UseClientTableResult<T> {
  const query = search?.query.trim().toLowerCase() ?? '';
  const getText = search?.getText;

  const filtered = useMemo(() => {
    if (!query || !getText) return data;
    return data.filter((row) => fuzzyMatch(getText(row).toLowerCase(), query));
  }, [data, query, getText]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  return { pageRows, total, pageCount };
}
